/**
 * AdminTrazabilidad.jsx · v101.9.0 · Logs y payloads de la app móvil y de las llamadas a Odoo.
 * Cada acción de la app tiene un "código de rastreo" (trace_id) que une:
 *   App móvil → servidor AlfaField → llamadas a Odoo (con el error COMPLETO de Odoo).
 */
import { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { RefreshCw, Search, X, Link2, Smartphone, Server, AlertTriangle, Loader2, ChevronRight, ChevronDown, Layers, List } from "lucide-react";

const KIND = {
  app: { label: "App → Servidor", icon: Smartphone, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  odoo_rpc: { label: "Servidor → Odoo", icon: Server, cls: "bg-purple-50 text-purple-700 border-purple-200" },
  app_client: { label: "Error en la app", icon: AlertTriangle, cls: "bg-amber-50 text-amber-800 border-amber-200" },
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-EC", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const Pretty = ({ value }) => {
  if (value === null || value === undefined || value === "") return <div className="text-xs text-gray-400 italic">—</div>;
  const txt = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  return (
    <pre className="text-[11px] leading-snug bg-gray-900 text-gray-100 rounded-lg p-3 overflow-auto max-h-80 whitespace-pre-wrap break-all">
      {txt}
    </pre>
  );
};

/** v101.163.0 · Fila de una OT + sus registros al expandir */
function FragmentRows({ g, open, onToggle, filas, Fila }) {
  const sinOt = g.odoo_task_id == null;
  return (
    <>
      <tr onClick={onToggle} className={`border-t cursor-pointer hover:bg-blue-50/40 ${g.errores ? "bg-red-50/30" : ""}`}
          data-testid={`trace-ot-${g.odoo_task_id ?? "sin"}`}>
        <td className="px-3 py-1.5">{open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</td>
        <td className="px-3 py-1.5 font-mono font-bold" style={{ color: "#0A3747" }}>
          {sinOt ? <span className="text-gray-500 font-sans">Sin OT (ping, login, inventario…)</span> : (g.task_number || g.odoo_task_id)}
        </td>
        <td className="px-3 py-1.5"><div className="truncate max-w-[200px]">{g.client_name || "—"}</div>
          <div className="text-[10px] text-gray-400">{g.internal_status || ""}</div></td>
        <td className="px-3 py-1.5 text-right tabular-nums">{g.total}</td>
        <td className={`px-3 py-1.5 text-right tabular-nums font-bold ${g.errores ? "text-red-700" : "text-green-700"}`}>{g.errores}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{g.odoo}{g.odoo_errores ? <span className="text-red-700 font-bold"> ({g.odoo_errores})</span> : ""}</td>
        <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">{fmtTime(g.ultimo)}</td>
        <td className="px-3 py-1.5 max-w-[280px] text-red-700">
          <div className="truncate" title={`${g.ultimo_error_en || ""} · ${g.ultimo_error || ""}`}>{g.ultimo_error || ""}</div>
          {g.ultimo_error_en && <div className="text-[10px] text-gray-400 font-mono truncate">{g.ultimo_error_en}</div>}
        </td>
        <td className="px-3 py-1.5 text-[10.5px] text-gray-600 max-w-[200px] truncate" title={(g.usuarios || []).join(", ")}>
          {(g.usuarios || []).join(", ")}
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50/60">
          <td colSpan={9} className="px-3 py-2">
            {sinOt ? (
              <div className="text-[11px] text-gray-500">Registros que no pertenecen a una OT. Véalos en la vista «Registros».</div>
            ) : !filas ? (
              <div className="text-[11px] text-gray-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Cargando registros de la OT…</div>
            ) : (
              <table className="w-full text-xs bg-white rounded">
                <tbody>{filas.map((it) => <Fila key={it.id} it={it} />)}</tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminTrazabilidad() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ kind: "", status: "", user: "", q: "", trace_id: "", hours: 24 });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // v101.163.0 · Vista agrupada por OT (por defecto) o lista de registros
  const [vista, setVista] = useState("ot");
  const [grupos, setGrupos] = useState([]);
  const [soloErr, setSoloErr] = useState(false);
  const [abierta, setAbierta] = useState(null);          // odoo_task_id expandido
  const [filasOt, setFilasOt] = useState({});            // {ot: items}

  const load = useCallback(async (filters = f) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) params[k] = v; });
      const v = filters.__vista || vista;
      const gp = { ...params };
      delete gp.trace_id;
      delete gp.__vista;
      delete params.__vista;
      if (soloErr) gp.solo_con_error = true;
      const [a, b] = await Promise.all([
        v === "ot" && !filters.trace_id
          ? api.get("/v1/admin/trace/por-ot", { params: gp })
          : api.get("/v1/admin/trace", { params }),
        api.get("/v1/admin/trace/summary", { params: { hours: filters.hours || 24 } }),
      ]);
      if (v === "ot" && !filters.trace_id) { setGrupos(a.data.items || []); setAbierta(null); setFilasOt({}); }
      else setItems(a.data.items || []);
      setSummary(b.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la trazabilidad");
    } finally {
      setLoading(false);
    }
  }, [f, vista, soloErr]);

  useEffect(() => { load(); }, [vista, soloErr]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOt = async (ot) => {
    if (abierta === ot) { setAbierta(null); return; }
    setAbierta(ot);
    if (ot == null || filasOt[ot]) return;
    try {
      const { data } = await api.get("/v1/admin/trace", { params: { ot, hours: f.hours || 24, limit: 500,
        ...(f.status ? { status: f.status } : {}), ...(f.kind ? { kind: f.kind } : {}) } });
      setFilasOt((m) => ({ ...m, [ot]: data.items || [] }));
    } catch { toast.error("No se pudieron cargar los registros de la OT"); }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const { data } = await api.get(`/v1/admin/trace/${id}`);
      setDetail(data);
    } catch {
      toast.error("No se pudo cargar el detalle");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const followTrace = (tid) => {
    const nf = { ...f, trace_id: tid, kind: "", status: "", user: "", q: "" };
    setF(nf);
    setDetail(null);
    setVista("lista");
    load({ ...nf, __vista: "lista" });
  };

  const Fila = ({ it }) => {
    const K = KIND[it.kind] || KIND.app;
    const Icon = K.icon;
    return (
      <tr onClick={() => openDetail(it.id)}
          className={`border-t cursor-pointer hover:bg-blue-50/40 ${it.status === "error" ? "bg-red-50/40" : ""}`}
          data-testid={`trace-row-${it.id}`}>
        <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">{fmtTime(it.ts)}</td>
        <td className="px-3 py-1.5">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${K.cls}`}><Icon size={11} />{K.label}</span>
        </td>
        <td className="px-3 py-1.5 truncate max-w-[160px]" title={it.user_email || ""}>{it.user_email || "—"}</td>
        <td className="px-3 py-1.5 font-mono truncate max-w-[280px]" title={it.target || it.path}>
          {it.kind === "odoo_rpc" ? it.target : `${it.method || ""} ${it.path || ""}`}
        </td>
        <td className="px-3 py-1.5">
          <span className={`px-1.5 py-0.5 rounded font-semibold ${it.status === "error" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
            {it.http_status || (it.status === "error" ? "ERROR" : "OK")}
          </span>
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums">{it.duration_ms ?? "—"}</td>
        <td className="px-3 py-1.5 truncate max-w-[260px] text-red-700" title={it.error || ""}>{it.error || ""}</td>
        <td className="px-3 py-1.5 font-mono">
          {it.trace_id ? (
            <button onClick={(e) => { e.stopPropagation(); followTrace(it.trace_id); }}
                    className="inline-flex items-center gap-1 text-blue-700 hover:underline" title="Ver toda la cadena">
              <Link2 size={11} />{it.trace_id.slice(0, 8)}
            </button>
          ) : "—"}
        </td>
      </tr>
    );
  };

  const errCount = (summary?.by_kind || []).filter((r) => r.status === "error").reduce((n, r) => n + r.n, 0);
  const totCount = (summary?.by_kind || []).reduce((n, r) => n + r.n, 0);

  return (
    <Layout>
      <div className="space-y-4" data-testid="trazabilidad-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "#0A3747" }}>Trazabilidad · App móvil y Odoo</h1>
            <p className="text-sm text-gray-500">
              Cada acción de la app con su payload, la respuesta del servidor y las llamadas a Odoo con su error completo.
              Contraseñas y tokens se ocultan. Retención: {summary?.retention_days ?? 7} días.
            </p>
          </div>
          <button onClick={() => load()} className="neu-btn" data-testid="trace-refresh">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refrescar
          </button>
        </div>

        {summary && !summary.enabled && (
          <div className="text-sm px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
            La trazabilidad está desactivada (TRACE_LOG_ENABLED=false).
          </div>
        )}

        {/* Resumen */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="neu-card p-4">
            <div className="text-xs text-gray-500">Registros ({f.hours} h)</div>
            <div className="text-2xl font-bold" style={{ color: "#0A3747" }}>{totCount}</div>
          </div>
          <div className="neu-card p-4">
            <div className="text-xs text-gray-500">Con error</div>
            <div className="text-2xl font-bold text-red-600">{errCount}</div>
          </div>
          <div className="neu-card p-4">
            <div className="text-xs text-gray-500 mb-1">Errores más frecuentes</div>
            {(summary?.top_errors || []).slice(0, 3).map((t, i) => (
              <div key={i} className="text-[11px] text-gray-700 truncate" title={`${t.what} · ${t.error}`}>
                <b>{t.n}×</b> {t.what} — {t.error || "sin detalle"}
              </div>
            ))}
            {!(summary?.top_errors || []).length && <div className="text-[11px] text-gray-400">Sin errores</div>}
          </div>
        </div>

        {/* Filtros */}
        <div className="neu-card p-3 flex flex-wrap gap-2 items-end">
          <div className="flex rounded-lg border overflow-hidden text-xs font-bold" data-testid="trace-vista">
            <button onClick={() => setVista("ot")} className={`px-2.5 py-1.5 flex items-center gap-1 ${vista === "ot" ? "bg-[#0A3747] text-white" : "bg-white text-gray-600"}`}>
              <Layers size={12} /> Por OT
            </button>
            <button onClick={() => setVista("lista")} className={`px-2.5 py-1.5 flex items-center gap-1 ${vista === "lista" ? "bg-[#0A3747] text-white" : "bg-white text-gray-600"}`}>
              <List size={12} /> Registros
            </button>
          </div>
          {vista === "ot" && (
            <label className="text-xs flex items-center gap-1 px-1">
              <input type="checkbox" checked={soloErr} onChange={(e) => setSoloErr(e.target.checked)} data-testid="trace-solo-err" />
              Solo OTs con error
            </label>
          )}
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="input text-sm" data-testid="trace-kind">
            <option value="">Todos los tipos</option>
            <option value="app">App → Servidor</option>
            <option value="odoo_rpc">Servidor → Odoo</option>
            <option value="app_client">Error en la app (sin respuesta)</option>
          </select>
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="input text-sm" data-testid="trace-status">
            <option value="">Todos los estados</option>
            <option value="error">Solo errores</option>
            <option value="ok">Solo OK</option>
          </select>
          <select value={f.hours} onChange={(e) => setF({ ...f, hours: Number(e.target.value) })} className="input text-sm">
            <option value={1}>Última hora</option>
            <option value={6}>6 horas</option>
            <option value={24}>24 horas</option>
            <option value={72}>3 días</option>
            <option value={168}>7 días</option>
          </select>
          <input value={f.user} onChange={(e) => setF({ ...f, user: e.target.value })} placeholder="Usuario (correo)"
                 className="input text-sm" data-testid="trace-user" />
          <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder={vista === "ot" ? "N° de OT, cliente o texto" : "Texto (ruta, modelo, error, payload)"}
                 className="input text-sm flex-1 min-w-[200px]" data-testid="trace-q" />
          <input value={f.trace_id} onChange={(e) => setF({ ...f, trace_id: e.target.value })} placeholder="Código de rastreo"
                 className="input text-sm font-mono w-44" data-testid="trace-id" />
          <button onClick={() => load()} className="neu-btn neu-btn-primary" data-testid="trace-search">
            <Search size={14} /> Buscar
          </button>
          {f.trace_id && (
            <button onClick={() => { const nf = { ...f, trace_id: "" }; setF(nf); load(nf); }} className="neu-btn">
              <X size={14} /> Quitar código
            </button>
          )}
        </div>

        {/* v101.163.0 · Vista agrupada por OT */}
        {vista === "ot" && !f.trace_id && (
          <div className="neu-card overflow-x-auto" data-testid="trace-por-ot">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="px-3 py-2 w-6"></th>
                  <th className="px-3 py-2 text-left">OT</th>
                  <th className="px-3 py-2 text-left">Cliente / estado</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                  <th className="px-3 py-2 text-right">Con error</th>
                  <th className="px-3 py-2 text-right">Odoo (error)</th>
                  <th className="px-3 py-2 text-left">Última</th>
                  <th className="px-3 py-2 text-left">Último error</th>
                  <th className="px-3 py-2 text-left">Usuarios</th>
                </tr>
              </thead>
              <tbody>
                {grupos.map((g) => {
                  const key = g.odoo_task_id ?? "sin";
                  const open = g.odoo_task_id != null ? abierta === g.odoo_task_id : abierta === "sin";
                  return (
                    <FragmentRows key={key} open={open} g={g}
                      onToggle={() => (g.odoo_task_id != null ? toggleOt(g.odoo_task_id) : setAbierta(abierta === "sin" ? null : "sin"))}
                      filas={g.odoo_task_id != null ? filasOt[g.odoo_task_id] : null} Fila={Fila} />
                  );
                })}
                {!grupos.length && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">{loading ? "Cargando…" : "Sin registros con estos filtros."}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Tabla */}
        {(vista === "lista" || f.trace_id) && (
        <div className="neu-card overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Hora</th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-left">Usuario</th>
                <th className="px-3 py-2 text-left">Qué</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-right">ms</th>
                <th className="px-3 py-2 text-left">Error</th>
                <th className="px-3 py-2 text-left">Rastreo</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => <Fila key={it.id} it={it} />)}
              {!items.length && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">{loading ? "Cargando…" : "Sin registros con estos filtros."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Detalle */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()}
               data-testid="trace-detail">
            <div className="flex items-center justify-between">
              <h2 className="font-bold" style={{ color: "#0A3747" }}>Detalle #{detail.id}</h2>
              <button onClick={() => setDetail(null)} className="p-1 rounded hover:bg-gray-100"><X size={16} /></button>
            </div>
            {detailLoading ? (
              <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Cargando…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-gray-500">Hora:</span> {fmtTime(detail.ts)}</div>
                  <div><span className="text-gray-500">Tipo:</span> {(KIND[detail.kind] || {}).label || detail.kind}</div>
                  <div><span className="text-gray-500">Usuario:</span> {detail.user_email || "—"}</div>
                  <div><span className="text-gray-500">Estado:</span> {detail.http_status || detail.status}</div>
                  <div className="col-span-2 font-mono break-all"><span className="text-gray-500 font-sans">Qué:</span> {detail.kind === "odoo_rpc" ? detail.target : `${detail.method || ""} ${detail.path || ""}`}</div>
                  <div><span className="text-gray-500">Duración:</span> {detail.duration_ms ?? "—"} ms</div>
                  <div><span className="text-gray-500">App:</span> {detail.app_version || "—"} · {detail.device || "—"}</div>
                  <div className="col-span-2 font-mono"><span className="text-gray-500 font-sans">Código de rastreo:</span> {detail.trace_id || "—"}
                    {detail.trace_id && (
                      <button onClick={() => followTrace(detail.trace_id)} className="ml-2 text-blue-700 hover:underline font-sans">
                        ver toda la cadena (app → servidor → Odoo)
                      </button>
                    )}
                  </div>
                </div>
                {detail.error && (
                  <div>
                    <div className="text-xs font-semibold text-red-700 mb-1">Error</div>
                    <Pretty value={detail.error} />
                  </div>
                )}
                <div>
                  <div className="text-xs font-semibold text-gray-700 mb-1">Payload enviado</div>
                  <Pretty value={detail.request} />
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-700 mb-1">Respuesta</div>
                  <Pretty value={detail.response} />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
